import { State } from 'redux/initialState';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Partner } from 'types';

import isSuperAdmin from 'utils/isSuperAdmin';
import { societyHasTitles } from 'utils/societyHasTitles';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { useGetSocietyShareTitleDocument } from 'modules/partners/pages/ShareTitleList/hooks/useGetSocietyShareTitleDocument';
import { useGetPartnerShares } from 'modules/partners/hooks/useGetPartnerShares';
import UpdateShares from 'modules/_shared/components/Modals/UpdateShares';

import sizes from 'constants/sizes';
import { PartnerSharesContextType } from '../types/PartnerSharesContextType';
import { partnerSharesInitValue } from '../constants/partnerSharesInitValue';

const PartnerSharesContext = createContext<PartnerSharesContextType>(
  partnerSharesInitValue
);

export const PartnerSharesProvider: FC<{
  children: ReactNode;
  partner?: Partner;
  isCoOwner?: boolean;
}> = ({ children, partner, isCoOwner }) => {
  const { societyId } = useParams();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state?.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);

  const societyShareTitlesConfiguration = useMemo(
    () => actualSociety?.configuration?.sharesTitles,
    [actualSociety]
  );

  const { data: societyShareTitleDocumentData, isLoading: isLoadingDocument } =
    useGetSocietyShareTitleDocument({ society: actualSociety });

  useGetActualSociety(societyId);
  useGetMenu();

  const { data: permissionsData } = useGetPartnersSectionPermissions();

  const { data: sharesData, isLoading: isLoadingShares } = useGetPartnerShares({
    partnerId: partner?._id || '',
    page,
    rowsPerPage,
    refetchKey,
    isCoOwner,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const invalidateShares = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleClickShare = (index: number) => {
    const share = sharesData?.shares?.[index];

    if (!share || (!permissionsData.isAdmin && !isSuperAdmin(user))) return;

    dispatch(
      setModal(
        <UpdateShares
          share={share}
          society={actualSociety}
          isReadOnly={isCoOwner}
          onUpdate={invalidateShares}
          size={sizes.XL}
        />
      )
    );
  };

  return (
    <PartnerSharesContext.Provider
      value={{
        data: {
          totalShares: isCoOwner
            ? sharesData?.totalShares || 0
            : partner?.sharesCount?.ranges || 0,
          shares: sharesData?.shares || [],
          sharesTitles: sharesData?.sharesTitles || [],
          societyShareTitleDocument:
            societyShareTitleDocumentData.document || null,
          societySharesTitleDocumentWithCoOwners:
            societyShareTitleDocumentData.documentWithCoOwners || null,
          societyId: actualSociety?._id || '',
          societyShareTitleTextPadding:
            societyShareTitlesConfiguration?.textPadding,
          page,
          rowsPerPage,
          hasSharesTitlesEnabled: societyHasTitles(actualSociety),
          isSocietyShareTitleLandscape:
            societyShareTitlesConfiguration?.isLandscape || false,
          isAdmin: permissionsData?.isAdmin || false,
          isCoOwner: isCoOwner || false,
          showEncumbranceColumn: permissionsData?.isAdmin && !isCoOwner,
          showSharesTitleColumn:
            societyHasTitles(actualSociety) &&
            (permissionsData?.isAdmin ||
              societyShareTitlesConfiguration?.isPartnerAllowedToDownload) &&
            !isCoOwner,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoadingShares,
          isLoading: isLoadingShares || isLoadingDocument,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleClickShare,
        },
      }}
    >
      {children}
    </PartnerSharesContext.Provider>
  );
};

export const usePartnerShares = () => {
  const context = useContext(PartnerSharesContext);
  if (!context) {
    throw new Error(
      'usePartnerShares must be used within a PartnerSharesProvider'
    );
  }
  return context;
};
